import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getMe,
  showApiError,
  updateMeProfile,
  updateMePassword,
} from '~/services';
import { fetchEntitlements } from '~/store/entitlements/thunks';
import type { PropsCoreState } from '~/store/interfaces';

import { mapApiUserToState } from './mappers';
import { userFailure, userRequest, userSetData, userSuccess } from './slices';

export const getUser = createAsyncThunk(
  'user/request',
  async (_, { dispatch }): Promise<boolean> => {
    try {
      dispatch(userRequest());
      const { status, data } = await getMe();
      if (status === 200) {
        dispatch(userSetData(mapApiUserToState(data)));
        dispatch(userSuccess());
        void dispatch(fetchEntitlements());
        return true;
      }
      dispatch(userFailure());
      return false;
    } catch {
      dispatch(userFailure());
      return false;
    }
  },
);

export const updateUserPersonalData = createAsyncThunk(
  'user/update-personal-data',
  async (
    { fullName, phone }: { fullName: string; phone: string },
    { dispatch, getState },
  ): Promise<boolean> => {
    try {
      dispatch(userRequest());
      const cleanedPhone = phone?.replace(/\D/g, '') ?? '';
      const { status } = await updateMeProfile({
        fullName,
        phone: cleanedPhone,
      });
      if (status === 200 || status === 204) {
        const currentEmail = (getState() as PropsCoreState).user.data.contact.email;
        dispatch(
          userSetData({
            fullName,
            contact: {
              email: currentEmail,
              phone: cleanedPhone,
            },
          }),
        );
        dispatch(userSuccess());
        return true;
      }
      dispatch(userFailure());
      return showApiError(new Error('Unexpected status'), {
        title: 'Não foi possível atualizar',
      });
    } catch (error) {
      dispatch(userFailure());
      return showApiError(error, { title: 'Não foi possível atualizar' });
    }
  },
);

export const changeMyPassword = createAsyncThunk(
  'user/change-password',
  async (
    payload: { currentPassword: string; newPassword: string },
    { dispatch },
  ): Promise<boolean> => {
    try {
      dispatch(userRequest());
      const { status } = await updateMePassword(payload);
      if (status === 200 || status === 204) {
        dispatch(userSuccess());
        return true;
      }
      dispatch(userFailure());
      return showApiError(new Error('Unexpected status'), {
        title: 'Não foi possível alterar a senha',
      });
    } catch (error) {
      dispatch(userFailure());
      return showApiError(error, { title: 'Não foi possível alterar a senha' });
    }
  },
);
